import {Maybe} from "graphql/jsutils/Maybe";
import {CmPictureFragment} from "../../packages/graphql-layer/src/queries/fragments/__generated__/CMPicture.fragment";
import {cdnCropping, Crops} from "../../packages/graphql-layer/src/__generated__/crops";
import {MEDIA_PATH, SERVICES} from "../common/constants/routes";

export const IMAGE_VERSION_PREVIEW = "latest";
export const IMAGE_VERSION_LIVE = "live";
export const ORIGINAL_FILE = "originalFile";
export const CDN_URL =  process.env.NEXT_PUBLIC_CDN_PROTOCOL + "://" + process.env.NEXT_PUBLIC_CDN_HOSTNAME + process.env.NEXT_PUBLIC_CDN_PATHNAME;

export const CDN_CROPPINGS: cdnCropping[] = [
    {
        cropName: Crops.portrait_ratio1x1.cropName,
        croppingArray: [Crops.portrait_ratio1x1.w540h540, Crops.portrait_ratio1x1.w1750h1750, Crops.portrait_ratio1x1.w890h890, Crops.portrait_ratio1x1.w1140h1140]
    },
    {
        cropName: Crops.landscape_ratio20x7.cropName,
        croppingArray: [Crops.landscape_ratio20x7.w2780h973, Crops.landscape_ratio20x7.w2080h728]
    },
    {
        cropName: Crops.portrait_ratio3x4.cropName,
        croppingArray: [Crops.portrait_ratio3x4.w780h1040]
    },
    {
        cropName: Crops.landscape_ratio4x3.cropName,
        croppingArray: [Crops.landscape_ratio4x3.w768h576]
    },
    {
        cropName: Crops.landscape_ratio16x9.cropName,
        croppingArray: [Crops.landscape_ratio16x9.w960h540, Crops.landscape_ratio16x9.w585h329, Crops.landscape_ratio16x9.w1440h810, Crops.landscape_ratio16x9.w320h180]
    }
]

/**
 * Größen, die in CDN_CROPPINGS für die URL-Auflösung stehen, die Celum aber nicht als CDN-Link
 * exportiert. Die Studio-Vorschau (C040Picture) zeigt sie nicht an, weil sie dort nur als leeres
 * Bild erscheinen würden.
 */
const CDN_PREVIEW_EXCLUDED_SIZES = [
    {cropName: Crops.portrait_ratio1x1.cropName, ...Crops.portrait_ratio1x1.w1140h1140},
    {cropName: Crops.landscape_ratio16x9.cropName, ...Crops.landscape_ratio16x9.w320h180}
];

export const CDN_PREVIEW_CROPPINGS: cdnCropping[] = CDN_CROPPINGS
    .map(crop => ({
        ...crop,
        croppingArray: crop.croppingArray.filter(size => !CDN_PREVIEW_EXCLUDED_SIZES.some(
            excluded => excluded.width === size.width && excluded.height === size.height
            && crop.cropName === excluded.cropName))
    }))
    .filter(crop => crop.croppingArray.length > 0);

export function getCertificateBackgroundUri(picture: Maybe<CmPictureFragment>): string {
    let backgroundUri;
    if (isCdnEnabled(picture)) {
        backgroundUri =  buildCdnUrl(picture, "original", "", false);
    }
    if (backgroundUri) {
        return SERVICES.CDN_BLOB + "?url=" + encodeURIComponent(backgroundUri);
    }
    if (hasFallbackUrl(picture) && picture?.urlPattern) {
        return picture.urlPattern.replace("/{cropName}/{width}", "");
    }
    if (picture && picture?.uriTemplate) {
        return MEDIA_PATH + picture?.uriTemplate;
    }
    return "";
}

export function isCdnEnabled(picture: Maybe<CmPictureFragment>): boolean {
    return !!(picture && picture.celumId && picture.celumId != 'disabled' && picture.celumEnv && picture.celumEnv != 'disabled');
}

export function hasFallbackUrl(picture: Maybe<CmPictureFragment>): boolean {
    return !!(picture && picture.celumId === 'disabled' && picture.celumEnv  === 'disabled' && picture.urlPattern && picture.urlPattern?.length > 0);
}

/**
 * Stammt die URL aus dem Celum-Export?
 *
 * Aufrufer setzen daraufhin `unoptimized` an `next/image`. Eine Celum-Rendition ist bereits in der
 * angeforderten Zielgröße und als WebP exportiert - der Bild-Optimizer würde sie ein zweites Mal
 * skalieren und neu kodieren, also dieselbe Überlegung wie im Lazy-Pfad von CCOResponsivePicture.
 *
 * Entscheidend ist hier aber sein Cache: Er legt das optimierte Bild unter (url, w, q) auf Platte ab
 * und hält es mindestens `minimumCacheTTL` lang - im Default vier Stunden. Nach einem Checkin ändert
 * der ?v=-Token die URL sofort, die neue Datei erscheint im Export aber erst bis zu eine Minute
 * später. Ein Abruf in diesem Fenster legt den *alten* Bildinhalt unter dem *neuen* Cache-Key ab,
 * und daran kommt danach weder ein Reload noch ein weiterer Checkin vorbei. Ohne Optimizer gibt es
 * keinen solchen Eintrag; der Browser holt direkt vom Blob.
 *
 * Über die Konfiguration ist das nicht lösbar: `minimumCacheTTL` gilt prozessweit, und die Vorschau
 * ist eine Route derselben Anwendung (src/pages/preview.tsx), kein eigenes Deployment.
 */
export function isCdnUrl(url: Maybe<string> | undefined): boolean {
    return !!url && url.startsWith(CDN_URL);
}

/**
 * Hängt den Cache-Busting-Token an eine CDN-URL.
 *
 * Rendition und Zielgröße bestimmen den Dateinamen vollständig, die URL bleibt also über alle
 * Zuschnitt-Änderungen hinweg identisch. Der Celum-Export liefert zudem kein Cache-Control, weshalb
 * der Next.js-Image-Optimizer auf seine minimumCacheTTL zurückfällt und ein geändertes Bild
 * stundenlang aus dem serverseitigen Cache bedient - kein Browser-Reload kommt dagegen an. Der Token
 * stammt aus dem Änderungsdatum des CMPicture (GraphQL: celumCacheKey) und erzeugt bei jeder neuen
 * Version einen neuen Cache-Key für Browser, Optimizer und CDN zugleich. Azure Blob Storage ignoriert
 * unbekannte Query-Parameter und liefert die Datei unverändert aus.
 */
export function withCacheKey(url: string, cacheKey: Maybe<string> | undefined): string {
    if (!cacheKey) {
        return url;
    }
    return url + (url.includes("?") ? "&" : "?") + "v=" + encodeURIComponent(cacheKey);
}

export function buildCdnUrl(picture: Maybe<CmPictureFragment>, orientation: string, width: string, isPreview: boolean): string | undefined {
    const celumId = picture?.celumId;
    const celumEnv = picture?.celumEnv;
    const celumFileExtension = picture?.celumFileExtension;
    if (!celumId || !celumEnv || !picture?.urlPattern || !celumFileExtension) {
        return undefined;
    }
    const cacheKey = picture?.celumCacheKey;
    let cdnUrl = CDN_URL + picture.urlPattern;
    cdnUrl = cdnUrl.replace("%env", celumEnv);
    cdnUrl = cdnUrl.replace("%id", celumId);
    let originalImg = cdnUrl.replace("%type", ORIGINAL_FILE);
    originalImg = originalImg.replace("%extension", celumFileExtension);

    if (orientation === "original") {
        return withCacheKey(originalImg, cacheKey);
    }

    cdnUrl = cdnUrl.replace("%extension", "webp");
    const version = isPreview ? IMAGE_VERSION_PREVIEW : IMAGE_VERSION_LIVE;
    const resolvedType = resolveType(orientation, width);
    if (!resolvedType) {
        return withCacheKey(originalImg, cacheKey);
    }
    const type = version + "/" + resolvedType;
    return withCacheKey(cdnUrl.replace("%type", type), cacheKey);
}

/**
 * Prefixes a resolved `Content[...]` uri (e.g. `globalFactsSettings.teaser_icon`) with `/media`,
 * unless it is already a complete Celum CDN URL (see `SosContentLinkResolver#resolveCmPictureLink`),
 * in which case it must be returned unchanged - analogous to `createPictureUri` in `CCOPicture.tsx`.
 */
export function resolveMediaUri(uri: string): string {
    if (uri.indexOf('soskinderdorfprodexport') > 0) {
        return uri;
    }
    return `/media${uri}`;
}

export function resolveType(orientation: string, strWidth: string): string | undefined {
    const crop =  CDN_CROPPINGS.find(({cropName}) => cropName === orientation);
    if (!crop) {
        return undefined;
    }
    const imageDimensions = crop.croppingArray.find(({width}) =>  parseInt(strWidth) == width);
    if (!imageDimensions) {
        console.warn(`Image dimensions not found for ${orientation} based on ${crop.cropName} and ${strWidth}. Returning original URL`);
        return undefined;
    }
    return imageDimensions?.width + "x" + imageDimensions?.height;
}
